import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AuthForm } from "@/components/auth/AuthForm";
import { DecisionProcessVisual } from "@/components/auth/DecisionProcessVisual";
import { DecisionProcessMobile } from "@/components/auth/DecisionProcessMobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import quentariLogo from "@/assets/quentari-logo.svg";
import { ArrowLeft, Loader2, Shield, Lock, Server } from "lucide-react";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  
  // Multi-stage animation states
  const [stageBg, setStageBg] = useState(false);
  const [stageGrid1, setStageGrid1] = useState(false);
  const [stageGrid2, setStageGrid2] = useState(false);
  const [stageHeader, setStageHeader] = useState(false);
  const [stageHeadline, setStageHeadline] = useState(false);
  const [stageSubtext, setStageSubtext] = useState(false);
  const [stageCard, setStageCard] = useState(false);
  const [stageVisual, setStageVisual] = useState(false);
  const [stageTrust, setStageTrust] = useState([false, false, false]);

  useEffect(() => {
    // Orchestrated entry sequence
    const timers = [
      setTimeout(() => setStageBg(true), 0),
      setTimeout(() => setStageGrid1(true), 150),
      setTimeout(() => setStageVisual(true), 200),
      setTimeout(() => setStageGrid2(true), 300),
      setTimeout(() => setStageHeader(true), 400),
      setTimeout(() => setStageHeadline(true), 500),
      setTimeout(() => setStageSubtext(true), 600),
      setTimeout(() => setStageCard(true), 700),
      setTimeout(() => setStageTrust([true, false, false]), 900),
      setTimeout(() => setStageTrust([true, true, false]), 1000),
      setTimeout(() => setStageTrust([true, true, true]), 1100),
    ];
    
    // Check if already logged in
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        navigate("/");
      }
    });
    
    return () => timers.forEach(clearTimeout);
  }, [navigate]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resetEmail) {
      toast({
        title: "Fehler",
        description: "Bitte gib deine E-Mail-Adresse ein.",
        variant: "destructive",
      });
      return;
    }

    setIsResetting(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast({
        title: "E-Mail versendet",
        description: "Überprüfe dein Postfach für den Zurücksetzungslink.",
      });

      setShowPasswordReset(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: "Fehler",
        description: error.message || "Ein Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Shared background component
  const AuthBackground = () => (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient for warmth */}
      <div 
        className="absolute inset-0 transition-opacity duration-1000"
        style={{
          opacity: stageBg ? 1 : 0,
          background: `
            radial-gradient(ellipse 80% 50% at 50% 0%, hsl(var(--primary) / 0.04) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 0% 50%, hsl(var(--primary) / 0.03) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 100% 50%, hsl(var(--primary) / 0.03) 0%, transparent 50%)
          `,
        }}
      />
      
      {/* Subtle radial pulse */}
      <div 
        className="absolute inset-0 animate-[subtle-pulse-bg_6s_ease-in-out_infinite]"
        style={{
          background: 'radial-gradient(circle at 50% 50%, hsl(var(--primary) / 0.06) 0%, transparent 60%)',
        }}
      />
      
      {/* Large grid - primary structure */}
      <div 
        className="absolute inset-[-20%] transition-all duration-1000"
        style={{
          opacity: stageGrid1 ? 1 : 0,
          transform: stageGrid1 ? 'scale(1)' : 'scale(0.98)',
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.05) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 40%, black 10%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 40%, black 10%, transparent 70%)',
        }}
      />
      
      {/* Small grid - detail layer */}
      <div 
        className="absolute inset-[-10%] transition-opacity duration-1000"
        style={{
          opacity: stageGrid2 ? 1 : 0,
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.02) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '15px 15px',
          maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 5%, transparent 60%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 5%, transparent 60%)',
        }}
      />
      
      {/* Intersection glow points */}
      <div 
        className="absolute inset-[-15%] transition-opacity duration-1000"
        style={{
          opacity: stageGrid2 ? 1 : 0,
          backgroundImage: `radial-gradient(circle at center, hsl(var(--primary) / 0.06) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 50% 50% at 50% 45%, black 0%, transparent 60%)',
          WebkitMaskImage: 'radial-gradient(ellipse 50% 50% at 50% 45%, black 0%, transparent 60%)',
        }}
      />
      
      {/* Decorative floating lines with varying opacity */}
      <div 
        className="absolute top-1/4 left-8 w-px h-24 bg-gradient-to-b from-transparent via-primary/15 to-transparent hidden md:block animate-[float_8s_ease-in-out_infinite,float-vary-opacity_6s_ease-in-out_infinite]"
      />
      <div 
        className="absolute top-1/3 right-8 w-px h-20 bg-gradient-to-b from-transparent via-primary/10 to-transparent hidden md:block animate-[float_10s_ease-in-out_infinite,float-vary-opacity_7s_ease-in-out_infinite]"
        style={{ animationDelay: '2s' }}
      />
      <div 
        className="absolute bottom-1/4 left-16 w-20 h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent hidden md:block animate-[float-horizontal_9s_ease-in-out_infinite,float-vary-opacity_5s_ease-in-out_infinite]"
        style={{ animationDelay: '1s' }}
      />
      
      {/* Vignette for depth */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 20%, hsl(var(--background)) 100%)`,
        }}
      />
    </div>
  );

  if (showPasswordReset) {
    return (
      <div className="min-h-screen bg-background flex flex-col relative">
        <AuthBackground />
        
        <header 
          className={`w-full border-b border-border/50 backdrop-blur-sm bg-background/80 relative z-10 transition-all duration-500 ${
            stageHeader ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
        >
          <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
            <Link to="/" className="transition-opacity hover:opacity-80">
              <img src={quentariLogo} alt="Quentari" className="h-8 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Zur Startseite
              </Button>
            </Link>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 md:px-6 pb-16 relative z-10">
          <div 
            className={`w-full max-w-md transition-all duration-700 ease-out ${
              stageCard ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-[0.96]'
            }`}
          >
            <Button
              variant="ghost"
              onClick={() => setShowPasswordReset(false)}
              className="mb-6 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück zur Anmeldung
            </Button>

            <div className="mb-8">
              <h1 
                className="text-2xl md:text-3xl font-semibold font-serif text-foreground mb-3"
                style={{ 
                  textShadow: `
                    0 1px 2px hsl(var(--foreground) / 0.04),
                    0 4px 8px hsl(var(--foreground) / 0.02)
                  `,
                }}
              >
                Passwort zurücksetzen
              </h1>
              <p className="text-muted-foreground">
                Gib deine E-Mail-Adresse ein, um einen Link zum Zurücksetzen zu erhalten.
              </p>
            </div>

            <div className="relative">
              {/* Card glow effect - animated */}
              <div 
                className="absolute -inset-px rounded-2xl auth-glow-rotate opacity-60"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--primary) / 0.12) 0%, transparent 40%, hsl(var(--primary) / 0.08) 60%, transparent 100%)',
                  backgroundSize: '200% 200%',
                }}
              />
              <div className="relative bg-card/80 backdrop-blur-sm border border-border/60 rounded-2xl p-8 shadow-xl shadow-primary/5">
                <form onSubmit={handlePasswordReset} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-sm font-medium">E-Mail</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="name@beispiel.de"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      disabled={isResetting}
                      className="h-12 bg-background/50 border-border/60 auth-input-focus"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 auth-shimmer-btn"
                    disabled={isResetting}
                  >
                    {isResetting && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                    {isResetting ? "Wird versendet..." : "Link senden"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const trustItems = [
    { icon: Shield, label: "DSGVO-konform" },
    { icon: Lock, label: "Verschlüsselte Daten" },
    { icon: Server, label: "EU-Hosting" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      <AuthBackground />
      
      {/* Header with slide-down */}
      <header 
        className={`w-full border-b border-border/50 backdrop-blur-sm bg-background/80 relative z-10 transition-all duration-500 ${
          stageHeader ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <Link to="/" className="transition-opacity hover:opacity-80">
            <img src={quentariLogo} alt="Quentari" className="h-8 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zur Startseite
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 md:px-6 py-8 md:py-16 relative z-10">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          
          {/* Left side: Claim + Visual */}
          <div 
            className={`hidden lg:flex flex-1 flex-col items-center justify-center transition-all duration-1000 ${
              stageVisual ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
            style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            {/* Meaning-giving headline above the visual */}
            <div className="text-center mb-8">
              <h2 
                className="text-2xl font-serif font-semibold text-foreground mb-2"
                style={{
                  animation: stageVisual ? 'blur-deblur 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards' : 'none',
                  opacity: 0,
                }}
              >
                Dein Entscheidungsraum.
              </h2>
              <p 
                className="text-sm text-muted-foreground"
                style={{
                  animation: stageVisual ? 'blur-deblur 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.6s forwards' : 'none',
                  opacity: 0,
                }}
              >
                Vier Phasen. Ein dokumentiertes Ergebnis.
              </p>
            </div>
            <DecisionProcessVisual />
          </div>

          {/* Right side: Auth form */}
          <div className="w-full max-w-sm">
            {/* Mobile process visual */}
            <div className="lg:hidden mb-6">
              <DecisionProcessMobile />
            </div>

            {/* Headline with blur-deblur */}
            <div className="mb-8 text-center lg:text-left">
              <h1 
                className="text-2xl sm:text-3xl md:text-4xl font-semibold font-serif text-foreground mb-2 relative"
                style={{ 
                  animation: stageHeadline ? 'blur-deblur 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'none',
                  opacity: stageHeadline ? undefined : 0,
                  filter: stageHeadline ? undefined : 'blur(8px)',
                  textShadow: `
                    0 1px 2px hsl(var(--foreground) / 0.04),
                    0 4px 8px hsl(var(--foreground) / 0.02),
                    0 8px 16px hsl(var(--primary) / 0.04)
                  `,
                }}
              >
                Klarheit beginnt hier.
              </h1>
              <p 
                className={`text-sm text-muted-foreground leading-relaxed transition-all duration-500 ${
                  stageSubtext ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
                style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
              >
                60 Minuten. Eine Entscheidung. Dokumentiert.
              </p>
            </div>

            {/* Auth card with scale-in + animated glow */}
            <div 
              className={`relative transition-all duration-700 ${
                stageCard ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-[0.96] translate-y-4'
              }`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              {/* Card glow effect - animated rotation */}
              <div 
                className="absolute -inset-px rounded-2xl auth-glow-rotate opacity-60"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--primary) / 0.12) 0%, transparent 40%, hsl(var(--primary) / 0.08) 60%, transparent 100%)',
                  backgroundSize: '200% 200%',
                }}
              />
              <div className="relative bg-card/80 backdrop-blur-sm border border-border/60 rounded-2xl p-6 shadow-xl shadow-primary/5">
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="relative grid grid-cols-2 h-12 w-full rounded-xl bg-muted/50 p-1.5 mb-6 border border-border/40">
                    <TabsTrigger 
                      value="login"
                      className="relative z-10 rounded-lg text-sm font-medium transition-all duration-200 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-md data-[state=active]:shadow-primary/10 data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/70"
                    >
                      Anmelden
                    </TabsTrigger>
                    <TabsTrigger 
                      value="signup"
                      className="relative z-10 rounded-lg text-sm font-medium transition-all duration-200 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-md data-[state=active]:shadow-primary/10 data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/70"
                    >
                      Registrieren
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="login" className="mt-0">
                    <AuthForm mode="login" />
                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={() => setShowPasswordReset(true)}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                      >
                        Passwort vergessen?
                      </button>
                    </div>
                  </TabsContent>

                  <TabsContent value="signup" className="mt-0">
                    <AuthForm mode="signup" />
                  </TabsContent>
                </Tabs>
              </div>
            </div>

            {/* Staggered trust indicators */}
            <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-xs text-muted-foreground/70">
              {trustItems.map((item, i) => (
                <span 
                  key={i}
                  className={`flex items-center gap-1.5 transition-all duration-500 ${
                    stageTrust[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                  }`}
                  style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
                >
                  <item.icon className="h-3.5 w-3.5" />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Auth;
